class CollectionMain extends HTMLElement {
  connectedCallback() {
    this.initGridControls();
    this.initFacets();
  }

  initGridControls() {
    const container = this.querySelector('#ProductGridContainer');
    if (!container) return;

    // #product-grid gets replaced wholesale whenever facets.js re-renders
    // the section after a filter/sort interaction (see
    // renderProductGridContainer in facets.js), so it's re-queried fresh
    // each time rather than captured once — a stale reference would keep
    // mutating a detached element that's no longer on screen.
    const applyColumns = () => {
      const grid = this.querySelector('#product-grid');
      // Mobile and desktop each have their own independent radio group, so
      // both can be :checked at once — only the one that's actually
      // visible for the current viewport (matching the breakpoint the CSS
      // uses to show/hide each list) should ever drive --grid-columns.
      const isMobileView = window.matchMedia('(max-width: 1024px)').matches;
      const name = isMobileView ? 'grid-columns-mobile' : 'grid-columns-desktop';
      const checked = this.querySelector(`input[name="${name}"]:checked`);
      if (!grid || !checked) return;

      grid.style.setProperty('--grid-columns', checked.value);

      // .collection__grid--compact hides the card's text/badges, leaving
      // just the media visible — the densest option in each context: 6
      // columns on desktop, 3 on mobile (2 stays "full" on mobile, same as
      // 4 does on desktop).
      const isDensestOption = checked.value === '6' || (isMobileView && checked.value === '3');
      grid.classList.toggle('collection__grid--compact', isDensestOption);
    };

    this.querySelectorAll('input[name="grid-columns-mobile"], input[name="grid-columns-desktop"]').forEach((input) => {
      input.addEventListener('change', applyColumns);
    });

    // Re-apply the shopper's current column choice to the new grid element
    // whenever one is swapped in, so it doesn't silently reset to the
    // server-rendered default after every filter/sort interaction.
    new MutationObserver(applyColumns).observe(container, { childList: true });

    applyColumns();
  }

  initFacets() {
    const facetsWrapper = this.querySelector('.facets-wrapper');
    const drawers = this.querySelectorAll('.mobile-facets__disclosure, .mobile-sort__disclosure');
    if (facetsWrapper && drawers.length) {
      const syncZIndex = () => {
        const anyOpen = [...drawers].some((d) => d.hasAttribute('open'));
        facetsWrapper.classList.toggle('drawer-is-open', anyOpen);
      };
      drawers.forEach((drawer) => {
        new MutationObserver(syncZIndex).observe(drawer, { attributes: true, attributeFilter: ['open'] });
      });
    }

    this.querySelectorAll('.mobile-facets__disclosure').forEach((details) => {
      const summary = details.querySelector('summary');
      const backdrop = details.querySelector('.mobile-facets');
      const closeBtn = details.querySelector('.mobile-facets__close');

      const close = () => {
        details.classList.add('is-closing');
        const onEnd = (event) => {
          if (event.target !== backdrop || event.propertyName !== 'opacity') return;
          backdrop.removeEventListener('transitionend', onEnd);
          details.removeAttribute('open');
          details.classList.remove('is-closing');
        };
        backdrop.addEventListener('transitionend', onEnd);
      };

      summary.addEventListener('click', (event) => {
        event.preventDefault();
        details.open ? close() : details.setAttribute('open', '');
      });

      closeBtn.addEventListener('click', close);

      backdrop.addEventListener('click', (event) => {
        if (!details.hasAttribute('open') || details.classList.contains('is-closing')) return;
        const inner = backdrop.querySelector('.mobile-facets__inner');
        if (inner && inner.contains(event.target)) return;
        close();
      });
    });

    this.querySelectorAll('.mobile-sort__disclosure').forEach((details) => {
      const summary = details.querySelector('summary');
      const backdrop = details.querySelector('.mobile-sort');
      const closeBtn = details.querySelector('.mobile-sort__close');

      const close = () => {
        details.classList.add('is-closing');
        const onEnd = (event) => {
          if (event.target !== backdrop || event.propertyName !== 'opacity') return;
          backdrop.removeEventListener('transitionend', onEnd);
          details.removeAttribute('open');
          details.classList.remove('is-closing');
        };
        backdrop.addEventListener('transitionend', onEnd);
      };

      summary.addEventListener('click', (event) => {
        event.preventDefault();
        details.open ? close() : details.setAttribute('open', '');
      });

      closeBtn.addEventListener('click', close);

      backdrop.addEventListener('click', (event) => {
        if (!details.hasAttribute('open') || details.classList.contains('is-closing')) return;
        const inner = backdrop.querySelector('.mobile-sort__inner');
        if (inner && inner.contains(event.target)) return;
        close();
      });
    });

    const filterSummary = this.querySelector('.mobile-facets__open-wrapper');
    const sortSummary = this.querySelector('.mobile-sort__open-wrapper');
    if (filterSummary && sortSummary) {
      sortSummary.style.top = filterSummary.getBoundingClientRect().top + 'px';
    }
  }
}

customElements.define('collection-main', CollectionMain);
